import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CLUB } from "@/lib/club";
import { isAdminLike } from "@/lib/roles";
import ProfileMenu from "@/components/ProfileMenu";
import PushBanner from "@/components/PushBanner";
import {
  LayoutDashboard, Calendar, FileText, Users, User, Wallet, LogOut, Shield, Target, Sparkles, ClipboardCheck, ScrollText, Trophy
} from "lucide-react";

const ALL_ROLES = ["super_admin", "admin", "admin_directive", "admin_treasurer", "coach", "player"];
const STAFF = ["super_admin", "admin", "admin_directive", "admin_treasurer", "coach"];
const ADMIN_ONLY = ["super_admin", "admin", "admin_directive", "admin_treasurer"];

export default function Layout({ children }) {
  const { user, logout, onboardingCompleted } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ALL_ROLES },
    { to: "/somos-ferro", label: "Somos Ferro!", icon: Sparkles, roles: ALL_ROLES },
    { to: "/calendar", label: "Calendario", icon: Calendar, roles: ALL_ROLES },
    { to: "/attendance", label: "Asistencia", icon: ClipboardCheck, roles: STAFF },
    { to: "/documents", label: "Documentos", icon: FileText, roles: ALL_ROLES },
    { to: "/roster", label: "Roster", icon: Users, roles: STAFF },
    { to: "/depth", label: "Depth Chart", icon: Target, roles: ALL_ROLES },
    { to: "/fixture", label: "Fixture", icon: Trophy, roles: ALL_ROLES },
    { to: isAdminLike(user?.role) ? "/finance" : "/dues", label: isAdminLike(user?.role) ? "Finanzas" : "Mis Cuotas", icon: Wallet, roles: ALL_ROLES },
    { to: "/profile", label: "Mi Ficha", icon: User, roles: ALL_ROLES },
    { to: "/admin", label: "Admin", icon: Shield, roles: ADMIN_ONLY },
    { to: "/admin/audit", label: "Auditoría", icon: ScrollText, roles: ["super_admin"] },
  ].filter(i => i.roles.includes(user?.role));

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-[#27272A] bg-[#0A0A0A]">
        <div className="px-6 py-6 border-b border-[#27272A] flex items-center gap-3">
          <img src={CLUB.logo} alt={CLUB.name} className="h-12 w-12 object-contain"/>
          <div>
            <div className="overline text-[#22d3ee]">{CLUB.city}</div>
            <div className="font-display text-xl text-white leading-none mt-1">{CLUB.name.toUpperCase()}</div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {items.map((it) => {
            const Active = location.pathname.startsWith(it.to);
            const Icon = it.icon;
            const disabled = !onboardingCompleted && it.to !== "/profile";
            const content = (
              <>
                <Icon size={16} />
                {it.label}
                {disabled && <span className="ml-auto text-[8px] text-[#FF9F0A]">🔒</span>}
              </>
            );
            if (disabled) {
              return (
                <div
                  key={it.to}
                  data-testid={`nav-disabled-${it.label.toLowerCase()}`}
                  className="flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-wider font-bold text-[#3f3f46] cursor-not-allowed border-l-2 border-transparent"
                  title="Completa tu ficha primero"
                >
                  {content}
                </div>
              );
            }
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-wider font-bold transition-colors ${
                  Active ? "text-white bg-[#1A1A1A] border-l-2 border-[#007AFF]" : "text-[#A1A1AA] hover:text-white hover:bg-[#121212] border-l-2 border-transparent"
                }`}
              >
                {content}
              </Link>
            );
          })}
        </nav>
        <ProfileMenu/>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0A0A0A] border-b border-[#27272A] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img src={CLUB.logo} alt={CLUB.name} className="h-8 w-8 object-contain"/>
          <div className="font-display text-lg text-white">FERROVIARIOS</div>
        </div>
        <button onClick={onLogout} className="btn-ghost text-xs flex items-center gap-1" data-testid="logout-button-mobile">
          <LogOut size={14}/> Salir
        </button>
      </div>

      <main className="flex-1 md:pt-0 pt-14 overflow-x-hidden">
        <PushBanner user={user}/>
        {children}
        {/* mobile bottom nav - horizontal scroll for all items */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-[#27272A] z-30 overflow-x-auto">
          <div className="flex min-w-max">
            {items.map((it) => {
              const Active = location.pathname.startsWith(it.to);
              const Icon = it.icon;
              const disabled = !onboardingCompleted && it.to !== "/profile";
              if (disabled) {
                return (
                  <div key={it.to} className="flex flex-col items-center py-2 px-4 min-w-[68px] text-[#3f3f46]">
                    <Icon size={18}/>
                    <span className="text-[9px] uppercase mt-1 whitespace-nowrap">{it.label}</span>
                  </div>
                );
              }
              return (
                <Link key={it.to} to={it.to} className={`flex flex-col items-center py-2 px-4 min-w-[68px] ${Active ? "text-[#007AFF]" : "text-[#A1A1AA]"}`}>
                  <Icon size={18}/>
                  <span className="text-[9px] uppercase mt-1 whitespace-nowrap">{it.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
